import { PregnancyWeekInfo } from "../types";

/** Standard pregnancy duration: 280 days (40 weeks) from last menstrual period */
const PREGNANCY_DURATION_DAYS = 280;

/** Calculate due date from last menstrual period using Naegele's rule (LMP + 280 days) */
export const calculateDueDate = (lastPeriodDate: string): string => {
  const lmp = new Date(lastPeriodDate + "T00:00:00");
  const due = new Date(lmp);
  due.setDate(due.getDate() + PREGNANCY_DURATION_DAYS);
  return due.toISOString().split("T")[0];
};

/** Get current pregnancy week info from dates */
export const getPregnancyWeekInfo = (
  dueDate: string,
  lastPeriodDate: string
): PregnancyWeekInfo => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lmp = new Date(lastPeriodDate + "T00:00:00");
  const due = new Date(dueDate + "T00:00:00");

  const totalDays = Math.max(
    0,
    Math.floor((today.getTime() - lmp.getTime()) / (1000 * 60 * 60 * 24))
  );
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  const daysUntilDue = Math.max(
    0,
    Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  );
  const percentComplete = Math.min(
    100,
    Math.round((totalDays / PREGNANCY_DURATION_DAYS) * 100)
  );

  const trimester: 1 | 2 | 3 = weeks < 13 ? 1 : weeks < 27 ? 2 : 3;

  return {
    weeks,
    days,
    trimester,
    totalDays,
    daysUntilDue,
    percentComplete,
    babySize: getBabySize(weeks),
  };
};

/** Fun fruit/object size comparison by week */
const getBabySize = (weeks: number): string => {
  const sizes: Record<number, string> = {
    4: "Poppy seed",
    5: "Sesame seed",
    6: "Lentil",
    7: "Blueberry",
    8: "Raspberry",
    9: "Cherry",
    10: "Strawberry",
    11: "Lime",
    12: "Plum",
    13: "Lemon",
    14: "Peach",
    15: "Apple",
    16: "Avocado",
    17: "Pear",
    18: "Sweet potato",
    19: "Mango",
    20: "Banana",
    21: "Carrot",
    22: "Papaya",
    23: "Grapefruit",
    24: "Corn on the cob",
    25: "Cauliflower",
    26: "Lettuce",
    27: "Cabbage",
    28: "Aubergine",
    29: "Butternut squash",
    30: "Coconut",
    31: "Pineapple",
    32: "Squash",
    33: "Cantaloupe",
    34: "Honeydew melon",
    35: "Coconut",
    36: "Papaya",
    37: "Winter melon",
    38: "Pumpkin",
    39: "Watermelon",
    40: "Jackfruit",
  };
  if (weeks < 4) return "Tiny poppy seed";
  if (weeks > 40) return sizes[40] || "Jackfruit";
  return sizes[weeks] || sizes[Math.max(...Object.keys(sizes).map(Number).filter(k => k <= weeks))] || "Growing strong";
};
